import { ButtonGroup, ConnectionItem, Flex, Image, Text } from "@wayflyer/flyui";
import { IconLotOfCash24Line, IconShop24Line, IconPage24Line } from "@wayflyer/flyui-icons/24/line";
import type { FundingApplicationSchemas } from "api-types";
import { useState } from "react";
import { useIntl, type IntlShape } from "react-intl";
import { filterDataSources } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { COMMON_MESSAGES } from "../../components/common-messages";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../hooks/navigation-next";
import { usePlatformConnectors } from "../../queries/connectors/queries";
import type { FormattedFundingApplicationScreen } from "../../queries/funding-application/queries";
import { useFundingApplication } from "../../queries/funding-application/queries";
import { isGoogleAnalyticsRequired } from "../../utils/funding-journey";
import {
  filterSelectedConnectors,
  getRequiredDocsFromConfiguration,
  getPlatformsForApplication,
} from "../../hooks/navigation-next/use-funding-application-navigation-next";

import { MESSAGES } from "./message-definitions";
import { OverviewSection } from "./overview-section";

const getOverviewItemForConnector = (connector: FundingApplicationSchemas.ConnectorPropertiesSchema) => {
  const logo = connector.connectionLogoSrc ? <Image src={connector.connectionLogoSrc} /> : <IconShop24Line />;

  return <ConnectionItem logo={logo} label={connector.name} key={connector.slug} />;
};

const getOverviewItemForAccountingConnector = (
  fundingApplication: FormattedFundingApplicationScreen,
  connector: FundingApplicationSchemas.ConnectorPropertiesSchema
) => {
  const logo =
    connector.connectionLogoSrc && connector.slug !== "codat_accounting" ? (
      <Image src={connector.connectionLogoSrc} />
    ) : (
      <IconShop24Line />
    );

  const displayName =
    connector.slug === "codat_accounting"
      ? fundingApplication.selectedUnsupportedAccounting.join(", ")
      : connector.name;
  return <ConnectionItem logo={logo} label={displayName} key={connector.slug} />;
};

const documentOptions = (
  intl: IntlShape,
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationConfiguration: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
): string[] => {
  const additionalDocuments = [];
  const requiredDocTypes = getRequiredDocsFromConfiguration(fundingApplication, fundingApplicationConfiguration);

  if (requiredDocTypes.includes("susa")) {
    additionalDocuments.push(intl.formatMessage(MESSAGES.documentUploadSusa));
  }
  if (requiredDocTypes.includes("pool-bancario")) {
    additionalDocuments.push(intl.formatMessage(MESSAGES.documentUploadPoolBancario));
  }
  if (requiredDocTypes.includes("profit-and-loss") || requiredDocTypes.includes("balance-sheet")) {
    additionalDocuments.push(intl.formatMessage(MESSAGES.documentUploadProfitAndLoss));
    additionalDocuments.push(intl.formatMessage(MESSAGES.documentUploadBalanceSheet));
  }
  if (requiredDocTypes.includes("debt-agreement")) {
    additionalDocuments.push(intl.formatMessage(MESSAGES.documentUploadDebtAgreement));
  }
  if (requiredDocTypes.includes("cash-flow-forecast")) {
    additionalDocuments.push(intl.formatMessage(COMMON_MESSAGES.documentUploadRevenueForecast));
  }

  return additionalDocuments;
};

interface ApplicationOverviewNextProps {
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
}
export const ApplicationOverviewNext = ({ fundingApplicationStages }: ApplicationOverviewNextProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const { data: connectors } = usePlatformConnectors(customerDomain, { skipConnections: true });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigationContext = useFundingApplicationNavigation({ currentPage: PAGE_TYPE.APPLICATION_OVERVIEW });

  // filtering is done to ensure order of connectors in the UI
  const {
    shoppingDataSources,
    analyticsDataSources,
    marketingDataSources,
    accountingDataSources,
    paymentsDataSources,
  } = filterDataSources(connectors, true);

  const showGAInNoAction =
    fundingApplication?.selectedConnectorSlugs?.includes("ga") &&
    !isGoogleAnalyticsRequired(fundingApplication.selectedConnectorSlugs);

  const connectorSlugsForJourney = getPlatformsForApplication(fundingApplication, fundingApplicationStages);

  const shoppingConnectors = filterSelectedConnectors(shoppingDataSources, connectorSlugsForJourney);
  const analyticsConnectors = filterSelectedConnectors(analyticsDataSources, connectorSlugsForJourney).filter(
    (connector) => !(connector.slug === "ga" && showGAInNoAction)
  );
  const marketingConnectors = filterSelectedConnectors(marketingDataSources, connectorSlugsForJourney);
  const accountingConnectors = filterSelectedConnectors(accountingDataSources, connectorSlugsForJourney);
  const paymentsConnectors = filterSelectedConnectors(paymentsDataSources ?? [], connectorSlugsForJourney);
  const gaConnector = showGAInNoAction ? connectors.find((connector) => connector.slug === "ga") : undefined;

  const sortedUnsupportedConnectors = [
    ...fundingApplication.selectedUnsupportedEcommerce,
    ...fundingApplication.selectedUnsupportedMarketingAnalytics,
    ...fundingApplication.selectedUnsupportedPayments,
  ];

  const requiredDocuments = documentOptions(intl, fundingApplication, fundingApplicationStages);

  return (
    <Flex direction="column" gap="8">
      <Text>{intl.formatMessage(MESSAGES.applicationOverviewDescription)}</Text>
      {requiredDocuments.length > 0 ? (
        <OverviewSection title={intl.formatMessage(MESSAGES.applicationOverviewUploadDocuments)}>
          {requiredDocuments.map((document, index) => (
            <ConnectionItem logo={<IconPage24Line />} label={document} key={index} />
          ))}
        </OverviewSection>
      ) : null}

      <OverviewSection title={intl.formatMessage(MESSAGES.applicationOverviewConnectPlatforms)}>
        <ConnectionItem
          logo={<IconLotOfCash24Line />}
          label={intl.formatMessage(MESSAGES.applicationOverviewAllBankAccountsLabel)}
        />
        {shoppingConnectors.map((connector) => getOverviewItemForConnector(connector))}
        {analyticsConnectors.map((connector) => getOverviewItemForConnector(connector))}
        {marketingConnectors.map((connector) => getOverviewItemForConnector(connector))}
        {accountingConnectors.map((connector) => getOverviewItemForAccountingConnector(fundingApplication, connector))}
        {paymentsConnectors.map((connector) => getOverviewItemForConnector(connector))}
      </OverviewSection>

      {sortedUnsupportedConnectors.length || showGAInNoAction ? (
        <OverviewSection
          title={intl.formatMessage(MESSAGES.applicationOverviewNoActionRequired)}
          description={intl.formatMessage(MESSAGES.applicationOverviewNoActionRequiredDescription)}
        >
          {sortedUnsupportedConnectors.length
            ? sortedUnsupportedConnectors.map((document, index) => (
                <ConnectionItem logo={<IconShop24Line />} label={document} key={index} />
              ))
            : null}
          {gaConnector ? getOverviewItemForConnector(gaConnector) : null}
        </OverviewSection>
      ) : null}

      <ButtonGroup
        primaryAction={{
          onAction: async () => {
            if (navigationContext.nextPath) {
              setIsSubmitting(true);
              await navigationContext.navigateWithStateUpdate(navigationContext.nextPath);
              setIsSubmitting(false);
            }
          },
          content: intl.formatMessage(MESSAGES.connectPlatformsGetStarted),
          analyticsId: "application-overview-continue-button",
          loading: isSubmitting,
        }}
        secondaryAction={{
          onAction: () => {
            if (navigationContext.previousPath) {
              navigationContext.navigate(navigationContext.previousPath);
            }
          },
          disabled: !navigationContext.previousPath,
          content: intl.formatMessage(COMMON_MESSAGES.backLabel),
          analyticsId: "application-overview-back-button",
        }}
        primaryActionTestId="navigation-continue-button"
        secondaryActionTestId="navigation-back-button"
      />
    </Flex>
  );
};
